export type ContextKey =
  | 'industry'
  | 'team_size'
  | 'offer'
  | 'location';

export interface ContextTag {
  key: ContextKey;
  value: string;
}

const LABELS: Record<ContextKey, string> = {
  industry: 'Industry',
  team_size: 'Size',
  offer: 'Offer',
  location: 'Location',
};

/**
 * Floating metadata badges that auto-build as Merlin identifies context
 * about the caller. Each key has exactly one slot — calling tag_context
 * with the same key replaces the value (with a flash animation).
 */
export class ContextBadges {
  private readonly root: HTMLElement;
  private readonly tags = new Map<ContextKey, ContextTag>();

  constructor(root: HTMLElement) {
    this.root = root;
    this.root.classList.add('context-badges');
  }

  add(tag: ContextTag) {
    const existing = this.tags.get(tag.key);
    this.tags.set(tag.key, tag);
    this.render();
    if (existing && existing.value !== tag.value) {
      const el = this.root.querySelector<HTMLElement>(
        `[data-key="${tag.key}"]`,
      );
      el?.classList.add('updated');
      window.setTimeout(() => el?.classList.remove('updated'), 420);
    } else {
      const el = this.root.querySelector<HTMLElement>(
        `[data-key="${tag.key}"]`,
      );
      el?.classList.add('enter');
      window.setTimeout(() => el?.classList.remove('enter'), 420);
    }
  }

  list(): ContextTag[] {
    return Array.from(this.tags.values());
  }

  clear() {
    this.tags.clear();
    this.render();
  }

  private render() {
    if (this.tags.size === 0) {
      this.root.innerHTML = '';
      this.root.classList.remove('has-tags');
      return;
    }
    this.root.classList.add('has-tags');
    this.root.innerHTML = Array.from(this.tags.values())
      .map(
        (t) => `
      <div class="context-badge" data-key="${t.key}">
        <span class="context-key">${escape(LABELS[t.key])}</span>
        <span class="context-value">${escape(t.value)}</span>
      </div>
    `,
      )
      .join('');
  }
}

function escape(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
